import { Parser } from '~/api';
import { CurrentMapFiles } from '~/api/models';
import RequestService from './request';

export interface MapRotation {
    id: string;
    sequence: number;
    size: number | null;
    seed: string | null;
    randomSeed: boolean;
    randomLevel: boolean;
    customUrl: string | null;
    lastUsedAt: string | null;
}

export interface FileDeletion {
    id: string;
    path: string;
}

export interface WipeSchedule {
    id: string;
    serverId: string;
    name: string;
    description: string;
    wipeBlueprints: boolean;
    nextRunAt: string;
    repeatAnchorAt: string | null;
    lastRunAt: string;
    isProcessing: boolean;
    isActive: boolean;
    isRepeating: boolean;
    repeatMode: 'cron' | 'biweekly' | 'force_wipe';
    timezone: string;
    repeatDays: number[];
    cronMinute: string;
    cronHour: string;
    cronDayOfMonth: string;
    cronMonth: string;
    cronDayOfWeek: string;
    formattedCron: string;
    createdAt: string;
    updatedAt: string;
    relationships?: {
        map_rotations?: MapRotation[];
        file_deletions?: FileDeletion[];
    };
}

interface CreateWipeScheduleRequest {
    name: string;
    description: string;
    wipe_blueprints: boolean;
    is_active: boolean;
    is_repeating: boolean;
    repeat_mode?: 'cron' | 'biweekly' | 'force_wipe';
    repeat_anchor_at?: string;
    timezone?: string;
    repeat_days: number[];
    map_rotations: Omit<MapRotation, 'id' | 'last_used_at'>[];
    file_deletions: Omit<FileDeletion, 'id'>[];
}

interface UpdateWipeScheduleRequest {
    id: number;
    name: string;
    description: string;
    wipe_blueprints: boolean;
    is_active: boolean;
    is_repeating: boolean;
    repeat_mode?: 'cron' | 'biweekly' | 'force_wipe';
    repeat_anchor_at?: string;
    timezone?: string;
    repeat_days: number[];
    map_rotations: Omit<MapRotation, 'last_used_at'>[];
    file_deletions: Omit<FileDeletion, 'id'>[];
}

interface DeleteWipeScheduleRequest {
    id: number;
}

interface TriggerWipeScheduleRequest {
    id: number;
}

interface ReactivateWipeScheduleRequest {
    id: number;
    schedule_date?: string;
}

class WipeScheduleService {
    getAll(req?: PaginatableRequest): Promise<ListResponse> {
        return RequestService.get('/servers/:server/wipe-schedules', {
            include: 'map_rotations,file_deletions',
            ...req,
        }).then(Parser.parse);
    }

    get(): Promise<WipeSchedule> {
        return RequestService.get('/servers/:server/wipe-schedules/:wipe_schedule', {
            include: 'map_rotations,file_deletions',
        }).then(Parser.parse);
    }

    create(data: CreateWipeScheduleRequest): Promise<WipeSchedule> {
        return RequestService.post('/servers/:server/wipe-schedules', data).then(Parser.parse);
    }

    update(data: UpdateWipeScheduleRequest): Promise<WipeSchedule> {
        return RequestService.patch(`/servers/:server/wipe-schedules/${data.id}`, data).then(Parser.parse).then(RequestService.updateModelBinding);
    }

    delete(data: DeleteWipeScheduleRequest): Promise<void> {
        return RequestService.delete(`/servers/:server/wipe-schedules/${data.id}`);
    }

    trigger(data: TriggerWipeScheduleRequest): Promise<void> {
        return RequestService.post(`/servers/:server/wipe-schedules/${data.id}/execute`);
    }

    reactivate(data: ReactivateWipeScheduleRequest): Promise<void> {
        return RequestService.post(`/servers/:server/wipe-schedules/${data.id}/reactivate`, data);
    }

    getCurrentMapFiles(id: string | number): Promise<CurrentMapFiles> {
        return RequestService.get(`/servers/:server/wipe-schedules/${id}/current-map-files`).then(Parser.parse);
    }
}

export default new WipeScheduleService();
